/**
 * Persona icons — the court (docs/design/IDENTITY.md § The court): King
 * Lothar, Prince Stef, and the human contender's Knight as bust icons in the
 * role-icon grammar. One grammar, three silhouettes: a shared head+shoulders
 * bust, told apart by the headgear (arched crown / pearled coronet / crested
 * great helm) plus one signature detail each — the King wears Lothar's
 * spectacles (the hint icon's "what Lothar sees"), the Prince's young face
 * carries two eyes, the Knight's cross-slit visor hides the challenger's face
 * (any face — yours). See docs/components/ICONOGRAPHY.md § Persona Icons.
 *
 * Proportions: the head owns the icon, the shoulders are a yoke under it. The
 * portrait cut (y=42) and the box are the same as the first cut; what changed
 * is the split between them — the shoulder arch lost half its depth and the
 * heads took the freed space. (The painted top edge isn't pinned the same
 * way: the King's crown finial used to sit at y=-0.3, clipped by the SVG's
 * own overflow, and now sits inside the box.)
 * Regalia scales independently of the head, so a bigger head reads as a bigger
 * head rather than as bigger headgear.
 *
 * Identity contract: a persona depicts a specific player, so — like the
 * claimed gatehouse — the whole figure carries the identity color (inline
 * styles, vellum rim underlays when dark via needsRim); the filled regalia
 * band rides the banner-cloth contract (identity fill, vellum rim). Without
 * identityColor everything draws currentColor as a mono glyph — the standard
 * family's actor/glyph switch (ICONOGRAPHY § The Standard Family).
 */
import { SVG_NS, svg, path, line, filledDot, filledPath } from "./svg.js";
import { needsRim } from "./role-icons.js";
import { IDENTITY_COLOR } from "../constants.js";

const VELLUM = "#E2E8F0";

function buildStroke(spec, w) {
  if (spec.d) return path(spec.d, w);
  if (spec.circle) {
    const [cx, cy, r] = spec.circle;
    const el = document.createElementNS(SVG_NS, "circle");
    el.setAttribute("cx", cx);
    el.setAttribute("cy", cy);
    el.setAttribute("r", r);
    el.setAttribute("stroke", "currentColor");
    el.setAttribute("stroke-width", w);
    el.setAttribute("fill", "none");
    return el;
  }
  return line(...spec.line, w);
}

/** Stroke pass with the gatehouse rim trick: dark identity strokes get a
 *  wider vellum twin underneath so they lift off Midnight Petrol. */
function addStrokes(s, specs, identityColor) {
  if (needsRim(identityColor)) {
    for (const spec of specs) {
      const rim = buildStroke(spec, spec.w + 1.5);
      rim.style.stroke = VELLUM;
      s.appendChild(rim);
    }
  }
  for (const spec of specs) {
    const el = buildStroke(spec, spec.w);
    if (identityColor) el.style.stroke = identityColor;
    s.appendChild(el);
  }
}

/** Filled regalia accent under the cloth contract: identity fill, vellum rim. */
function addRegaliaFill(s, d, identityColor) {
  const el = filledPath(d);
  if (identityColor) {
    el.style.fill = identityColor;
    el.style.stroke = VELLUM;
    el.setAttribute("stroke-width", "1");
    el.setAttribute("stroke-linejoin", "round");
  }
  s.appendChild(el);
}

/** Small filled orbs (finial dots on regalia, the Prince's eyes); vellum-ringed
 *  when identity-colored so dark ones stay visible on petrol. */
function addOrbs(s, orbs, identityColor, rimWidth = "0.8") {
  for (const [cx, cy, r] of orbs) {
    const el = filledDot(cx, cy, r);
    if (identityColor) {
      el.style.fill = identityColor;
      el.style.stroke = VELLUM;
      el.setAttribute("stroke-width", rimWidth);
    }
    s.appendChild(el);
  }
}

// ---------------------------------------------------------------------------
// Shared bust geometry
// ---------------------------------------------------------------------------

const CUT = 42; // why: the portrait cut — the bust's bottom edge, the box's fixed floor
const HEAD_BOTTOM = 33.25; // why: where the head meets the yoke apex, 2 units above it
const YOKE_DEPTH = 6.75; // why: half the original arch's depth — the point of the recut
const BAND_OVERLAP = 1.5; // why: regalia drops onto the head-top so headgear reads worn
const BASE_HEAD_R = 6.5; // why: the first cut's head radius — face detail scales off it

const round = (n) => Math.round(n * 100) / 100;

/**
 * The shoulder yoke: the original arch re-cut to `halfWidth` at YOKE_DEPTH.
 * Width is per-persona because the yoke has to clear whatever sits on it or it
 * stops reading as shoulders — and the Knight's helm needs more clearance than
 * a head circle does, presenting a vertical flank where the circle offers only
 * a tangent.
 */
function yoke(halfWidth) {
  const x = (dx) => round(24 + (dx * halfWidth) / 15);
  const apex = CUT - YOKE_DEPTH;
  const slope = round(CUT - (YOKE_DEPTH * 9.5) / 13.5); // why: the original arch's control-point ratio, preserved so the recut curve keeps the same character
  return `M${x(-15)} ${CUT} C${x(-14)} ${slope} ${x(-7.5)} ${apex} 24 ${apex} C${x(7.5)} ${apex} ${x(14)} ${slope} ${x(15)} ${CUT}`;
}

/**
 * Regalia host: a group scaled on its own and dropped onto the head-top, so a
 * persona's headgear keeps its absolute presence while the head grows under it.
 * `bandBottom` is the regalia's own baseline in its untransformed coordinates.
 */
function regaliaGroup(s, scale, headTop, bandBottom) {
  const g = document.createElementNS(SVG_NS, "g");
  const ty = round(headTop + BAND_OVERLAP - bandBottom * scale);
  g.setAttribute(
    "transform",
    `translate(${round(24 * (1 - scale))} ${ty}) scale(${scale})`,
  );
  s.appendChild(g);
  return g;
}

/** Head circle + yoke: the two strokes every persona shares. */
function bustSpecs(headR, yokeHalfWidth) {
  const cy = round(HEAD_BOTTOM - headR);
  const s = headR / BASE_HEAD_R;
  return {
    cy,
    s,
    specs: [
      { circle: [24, cy, headR], w: round(2.5 * s) },
      { d: yoke(yokeHalfWidth), w: 2.5 },
    ],
  };
}

// Per-persona proportions. The Prince carries the biggest head because he
// carries the least headgear — the mass the King's crown and the Knight's helm
// supply has to come from somewhere, or he reads smaller than the other two.
const KING_HEAD_R = 8.6;
const KING_CROWN_SCALE = 1.05; // held near 1.0: the crown must not grow with the head
const PRINCE_HEAD_R = 9;
const PRINCE_CORONET_SCALE = 1.1;
const KNIGHT_HELM_SCALE = 1.2; // helm is head and headgear in one, so it scales as a whole
const KNIGHT_YOKE_HALF = 15;

/** Lothar's spectacles — two round lenses under an arched bridge, scaled with
 *  the head (already the hint icon's "what Lothar sees"). */
function kingSpectacleSpecs(cy, s) {
  const lens = round(2 * s);
  const eyeY = round(cy - 0.2 * s);
  return [
    { circle: [round(24 - 2.8 * s), eyeY, lens], w: round(1.7 * s) },
    { circle: [round(24 + 2.8 * s), eyeY, lens], w: round(1.7 * s) },
    {
      d: `M${round(24 - 0.8 * s)} ${round(cy - 0.7 * s)} Q24 ${round(cy - 1.6 * s)} ${round(24 + 0.8 * s)} ${round(cy - 0.7 * s)}`,
      w: round(1.4 * s),
    },
  ];
}

/** The three-peak arched crown, its band, and pearl finials — dropped onto
 *  the head-top at its own near-1.0 scale (KING_CROWN_SCALE). */
function addKingCrown(el, cy, identityColor) {
  const crown = regaliaGroup(el, KING_CROWN_SCALE, round(cy - KING_HEAD_R), 15);
  addStrokes(
    crown,
    [
      // Crown peaks — center peak taller, echoing the König motif's spires
      {
        d: "M16 12 L18.5 3.5 L22 12 M22 12 L24 1.5 L26 12 M26 12 L29.5 3.5 L32 12",
        w: 2,
      },
    ],
    identityColor,
  );
  addRegaliaFill(crown, "M16 15 L16 12 L32 12 L32 15 Z", identityColor);
  addOrbs(
    crown,
    [
      [18.5, 2.6, 1],
      [24, 0.8, 1.1],
      [29.5, 2.6, 1],
    ],
    identityColor,
  );
}

/** King Lothar — three-peak arched crown (the König card crown's pen, worn
 *  by its wearer) over round spectacles: the King who taught the game.
 *  No beard — the real Lothar didn't wear one; the spectacles alone are
 *  the likeness. */
export function createPersonaKing(size, identityColor) {
  const el = svg(size, size);
  const { cy, s, specs } = bustSpecs(KING_HEAD_R, KING_HEAD_R + 3.6); // why: +3.6 is the crown's own clearance over the head radius
  addStrokes(el, [...specs, ...kingSpectacleSpecs(cy, s)], identityColor);
  addKingCrown(el, cy, identityColor);
  return el;
}

/** Two eyes, drawn as filled dots rather than rings so they stay unmistakably
 *  his beside the King's spectacles at chip size. */
function addPrinceEyes(el, cy, s, identityColor) {
  const eyeX = round(2.6 * s);
  const eyeY = round(cy - 0.3 * s);
  addOrbs(
    el,
    [
      [round(24 - eyeX), eyeY, round(1.25 * s)],
      [round(24 + eyeX), eyeY, round(1.25 * s)],
    ],
    identityColor,
    "0.9", // why: a hair wider than the finials' default 0.8 rim — the eyes are the
    // face's own accent, not a regalia dot, so they get a touch more presence.
  );
}

/** The pearled open coronet — pearl stems, band, and finial pearls — dropped
 *  onto the head-top at its own near-1.0 scale (PRINCE_CORONET_SCALE). */
function addPrinceCoronet(el, cy, identityColor) {
  const coronet = regaliaGroup(
    el,
    PRINCE_CORONET_SCALE,
    round(cy - PRINCE_HEAD_R),
    15,
  );
  addStrokes(
    coronet,
    [
      // Pearl stems rising off the circlet
      { line: [19.5, 12.4, 19.5, 11.2], w: 1.2 },
      { line: [24, 12.4, 24, 10.6], w: 1.2 },
      { line: [28.5, 12.4, 28.5, 11.2], w: 1.2 },
    ],
    identityColor,
  );
  addRegaliaFill(coronet, "M17 15 L17 12.4 L31 12.4 L31 15 Z", identityColor);
  addOrbs(
    coronet,
    [
      [19.5, 10.2, 1.2],
      [24, 9.4, 1.4],
      [28.5, 10.2, 1.2],
    ],
    identityColor,
  );
}

/** Prince Stef — pearled open coronet worn low on the brow over a young face:
 *  two eyes, drawn as filled dots rather than rings so they stay unmistakably
 *  his beside the King's spectacles at chip size. */
export function createPersonaPrince(size, identityColor) {
  const el = svg(size, size);
  const { cy, s, specs } = bustSpecs(PRINCE_HEAD_R, PRINCE_HEAD_R + 3.6); // why: +3.6 is the coronet's own clearance over the head radius
  addStrokes(el, specs, identityColor);
  addPrinceEyes(el, cy, s, identityColor);
  addPrinceCoronet(el, cy, identityColor);
  return el;
}

/** The great helm's own scaled group — head and headgear in one, so it scales
 *  as a whole rather than splitting off a separate regalia group. Its jaw cut
 *  (the dome path's own y=28, unscaled) lands 0.5 above the yoke apex — the
 *  same gap the un-scaled helm held before the recut — hence `HEAD_BOTTOM +
 *  1.5`: HEAD_BOTTOM already sits 2 above the apex, and the jaw wants only
 *  0.5, so the translate gives back the other 1.5. */
function helmGroup(el, scale) {
  const helm = document.createElementNS(SVG_NS, "g");
  helm.setAttribute(
    "transform",
    `translate(${round(24 * (1 - scale))} ${round(HEAD_BOTTOM + 1.5 - 28 * scale)}) scale(${scale})`,
  );
  el.appendChild(helm);
  return helm;
}

/** The Knight — the human contender: crested great helm with the crusader
 *  cross-slit visor, seated on the shoulders. The hidden face is the point —
 *  it's anyone's; yours. */
export function createPersonaKnight(size, identityColor) {
  const el = svg(size, size);
  addStrokes(el, [{ d: yoke(KNIGHT_YOKE_HALF), w: 2.5 }], identityColor);
  const helm = helmGroup(el, KNIGHT_HELM_SCALE);
  addStrokes(
    helm,
    [
      // Great helm: rounded dome, jaw cut meeting the shoulder line
      {
        d: "M15.5 28 L15.5 13.5 Q15.5 6 24 6 Q32.5 6 32.5 13.5 L32.5 28 Z",
        w: 2.5,
      },
      // Cross-slit visor: eye slit + breathing cross
      { line: [18.5, 16, 29.5, 16], w: 2 },
      { line: [24, 16, 24, 23.5], w: 2 },
    ],
    identityColor,
  );
  // Crest ridge, riding clear of the dome rather than resting on it — the one
  // regalia piece that doesn't overlap what it's worn on. why: the crest carries
  // the identity fill inside a vellum rim, and the human's identity IS near-vellum
  // white, so a crest touching the dome has no edge of its own and reads as a lump.
  // The gap gives it a silhouette that survives the light identity and 34px.
  addRegaliaFill(
    helm,
    "M16 6.3 Q24 -5.2 32 6.3 Q24 1.3 16 6.3 Z",
    identityColor,
  );
  return el;
}

/** Screen seat → persona, mirroring the fixed court of IDENTITY § The three
 *  identity layers: the left opponent is Lothar the King, the right is Stef the
 *  Prince, the human seat is the contender (first persona: the Knight — it
 *  depicts the seat's contender, not the entered name, so it shows regardless
 *  of the name slot). Keyed like IDENTITY_COLOR (constants.js), so consumers
 *  pair persona and color off the same screen-seat key. */
export const PERSONA_BY_SEAT = {
  left: createPersonaKing,
  right: createPersonaPrince,
  human: createPersonaKnight,
};

// The court's fixed identities (SkatSession: seat 0 = the human/Knight, 1 = Lothar/King, 2 =
// Stef/Prince). Screen-left is always seat 1 and screen-right always seat 2 — the clockwise seat
// order is fixed, only the dealer role rotates — so a player's persona and identity colour are
// seat-stable here, no per-round screen-slot math (unlike the live stage's screenPositionFor).
// Promoted from games-list-enhance.js (round-inspector-show-dont-tell): the round inspector is the
// second numeric-seat consumer, which earns the shared home (CODING: a second concrete use earns
// the abstraction).
export const SEAT_PERSONA = [
  createPersonaKnight,
  createPersonaKing,
  createPersonaPrince,
];
export const SEAT_IDENTITY_COLOR = [
  IDENTITY_COLOR.human,
  IDENTITY_COLOR.left,
  IDENTITY_COLOR.right,
];
